import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/synapse/Icon';
import { InitialsAvatar } from '@/components/synapse/Avatar';
import {
  captureVideoFrame,
  fileToJpeg,
  PHOTO_CONTENT_TYPE,
} from '@/features/patients/photo';

export interface CapturedPhoto {
  blob: Blob;
  contentType: string;
  /** Object URL for previewing the captured blob. Caller owns revocation. */
  previewUrl: string;
}

interface PhotoCaptureProps {
  /** Name used for the initials fallback when there is no photo. */
  name?: string;
  /** What to show now: a freshly captured object URL or an existing photo URL. */
  previewUrl?: string | null;
  /** Fired with the normalized JPEG when the user captures/picks an image. */
  onCapture: (photo: CapturedPhoto) => void;
  /** Fired when the user clears the current photo. */
  onRemove?: () => void;
  /** Disable all controls (e.g. while an upload is in flight). */
  busy?: boolean;
  size?: number;
}

/**
 * Patient photo control: a round preview with a camera (in-app getUserMedia
 * modal — works on desktop webcams and mobile) and a file/gallery picker. Both
 * paths normalize to a bounded JPEG before handing the blob up. Used both at
 * registration and for changing an existing patient's photo.
 */
export function PhotoCapture({
  name,
  previewUrl,
  onCapture,
  onRemove,
  busy = false,
  size = 96,
}: PhotoCaptureProps) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    // Allow re-selecting the same file later by clearing the input value.
    e.target.value = '';
    if (!file) return;
    setError(null);
    try {
      const blob = await fileToJpeg(file);
      onCapture({ blob, contentType: PHOTO_CONTENT_TYPE, previewUrl: URL.createObjectURL(blob) });
    } catch {
      setError(t('patients.photo.readFailed'));
    }
  }

  function handleCaptured(blob: Blob) {
    setCameraOpen(false);
    setError(null);
    onCapture({ blob, contentType: PHOTO_CONTENT_TYPE, previewUrl: URL.createObjectURL(blob) });
  }

  return (
    <div className="flex items-center gap-4">
      <InitialsAvatar name={name} photoUrl={previewUrl ?? undefined} size={size} />

      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="secondary"
            onClick={() => {
              setError(null);
              setCameraOpen(true);
            }}
            disabled={busy}
          >
            <Icon name="scan" size={15} />
            {t('patients.photo.takePhoto')}
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() => fileInputRef.current?.click()}
            disabled={busy}
          >
            <Icon name="plus" size={15} />
            {t('patients.photo.choose')}
          </Button>
          {previewUrl && onRemove && (
            <Button type="button" variant="ghost" onClick={onRemove} disabled={busy}>
              <Icon name="trash" size={15} />
              {t('patients.photo.remove')}
            </Button>
          )}
        </div>
        <p className="text-[12px] text-muted">{t('patients.photo.hint')}</p>
        {error && (
          <p className="text-[12px] text-destructive" role="alert">
            {error}
          </p>
        )}
      </div>

      {/* Hidden input drives the file/gallery path. On mobile, accept=image/*
          surfaces both the camera roll and the camera itself. */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFile}
      />

      {cameraOpen && (
        <CameraModal onCapture={handleCaptured} onClose={() => setCameraOpen(false)} />
      )}
    </div>
  );
}

export interface CameraModalProps {
  onCapture: (blob: Blob) => void;
  onClose: () => void;
}

/**
 * Live-camera capture modal. Requests the camera on open, draws a frame to a
 * canvas on "Capture", and hands back a bounded JPEG. On any camera failure
 * (permission denied, no device, insecure context) it shows an actionable
 * message and lets the user close and fall back to the file picker.
 */
export function CameraModal({ onCapture, onClose }: CameraModalProps) {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  // The deviceId of the camera the live stream is actually using. Used to
  // pick the "next" camera when cycling; kept in a ref so reading it in the
  // switch handler doesn't re-run the acquisition effect.
  const activeDeviceIdRef = useRef<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const [working, setWorking] = useState(false);
  // Available video inputs (only labelled once permission is granted) and the
  // explicitly chosen one. `null` means "let the browser pick" (facingMode).
  const [cameras, setCameras] = useState<MediaDeviceInfo[]>([]);
  const [deviceId, setDeviceId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function start() {
      if (!navigator.mediaDevices?.getUserMedia) {
        setError(t('patients.photo.cameraUnsupported'));
        return;
      }
      try {
        setReady(false);
        const stream = await navigator.mediaDevices.getUserMedia({
          // On the first open, no camera is chosen yet: staff photograph the
          // patient in front of them, so prefer the rear camera on phones;
          // desktops with only a front webcam fall back to it. Once the user
          // picks a specific camera we pin it by deviceId.
          video: deviceId ? { deviceId: { exact: deviceId } } : { facingMode: { ideal: 'environment' } },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        // Replace any previous stream (e.g. when switching cameras).
        streamRef.current?.getTracks().forEach((track) => track.stop());
        streamRef.current = stream;
        activeDeviceIdRef.current = stream.getVideoTracks()[0]?.getSettings().deviceId ?? deviceId;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
          setReady(true);
        }
        // Labels and deviceIds are only exposed after permission is granted,
        // so enumerate here rather than before the first getUserMedia call.
        const inputs = (await navigator.mediaDevices.enumerateDevices()).filter(
          (d) => d.kind === 'videoinput',
        );
        if (!cancelled) setCameras(inputs);
      } catch (err) {
        const name = (err as { name?: string })?.name;
        setError(
          name === 'NotAllowedError' || name === 'SecurityError'
            ? t('patients.photo.cameraDenied')
            : t('patients.photo.cameraUnavailable'),
        );
      }
    }

    void start();
    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    };
  }, [deviceId, t]);

  function switchCamera() {
    if (cameras.length < 2) return;
    const current = cameras.findIndex((c) => c.deviceId === activeDeviceIdRef.current);
    const next = cameras[(current + 1) % cameras.length];
    if (next) setDeviceId(next.deviceId);
  }

  async function capture() {
    const video = videoRef.current;
    if (!video || !ready) return;
    setWorking(true);
    try {
      const blob = await captureVideoFrame(video);
      onCapture(blob);
    } catch {
      setError(t('patients.photo.captureFailed'));
      setWorking(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('patients.photo.takePhoto')}
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-md rounded-t-2xl bg-paper p-5 sm:rounded-2xl">
        <div className="flex items-center justify-between">
          <div className="text-[15px] font-semibold tracking-[-0.01em]">
            {t('patients.photo.takePhoto')}
          </div>
          <div className="flex items-center gap-1">
            {!error && cameras.length > 1 && (
              <button
                type="button"
                onClick={switchCamera}
                disabled={!ready}
                aria-label={t('patients.photo.switchCamera')}
                title={t('patients.photo.switchCamera')}
                className="flex h-8 w-8 items-center justify-center rounded-md text-muted hover:bg-paper-2 hover:text-ink disabled:opacity-50"
              >
                <Icon name="camera_switch" size={18} />
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              aria-label={t('common.close')}
              className="flex h-8 w-8 items-center justify-center rounded-md text-muted hover:bg-paper-2 hover:text-ink"
            >
              <Icon name="x" size={18} />
            </button>
          </div>
        </div>

        {error ? (
          <p className="mt-3.5 rounded-lg border border-line bg-destructive-bg/40 px-3 py-3 text-[12.5px] text-destructive">
            {error}
          </p>
        ) : (
          <div className="mt-3.5 overflow-hidden rounded-lg border border-line bg-black">
            <div className="relative aspect-[4/3] w-full">
              <video
                ref={videoRef}
                playsInline
                muted
                className="h-full w-full object-cover"
              />
            </div>
          </div>
        )}

        <div className="mt-4 flex gap-2">
          <Button variant="secondary" full onClick={onClose}>
            {t('common.cancel')}
          </Button>
          {!error && (
            <Button full disabled={!ready || working} onClick={capture}>
              <Icon name="check" size={16} />
              {t('patients.photo.capture')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
